import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import './OnboardedCustomertable.css';
import TabNavigation, { TabConfig } from '../../Components/common/TabNavigation';
import SearchIcon from '../../Images/search-icon.svg';
import DownloadIcon from '../../Images/download-icon.svg';
import ArrowUp from '../../Images/chevron-up.svg';
import ArrowDown from '../../Images/chevron-down.svg';
import ArrowDefault from '../../Images/Chevron-default-icon.svg';
import Loader from '../../Components/common/loader/loader';
import { useNavigate, useLocation } from 'react-router-dom';
import Pagination from '../../Components/common/Pagination/Pagination';
import CreateAccountModal from './CreateCustomerModal';
import useSearch from '../../hooks/useSearch';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
    fetchCustomerList,
    downloadCustomers,
    selectCustomers,
    selectCustomersLoading,
    selectCustomersError,
    selectDownloadError,
    selectCreateError,
    resetCustomers,
    clearError
} from './OnboardedCustomertableSlice';
import { getSalesDistrict } from '../../Services/serviceURL';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

// Define your interfaces here
export interface OnboardedCustomerData {
    customer: string;
    cnxId: string;
    soldTo: string;
    country: string;
    salesDistrict: string;
    salesRegion: string;
    state: string;
    city: string;
}

interface SalesDistrictItem {
    id: number;
    salesRegionId: number;
    salesRegion: string;
    code: string;
    name: string;
    description: string | null;
    active: boolean;
}

type SortableKeys = keyof OnboardedCustomerData;

const OnboardedCustomerListTable: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Tab configuration
    const tabConfig: TabConfig[] = [
        { key: 'Plants', label: 'Plants', route: '/plants' },
        { key: 'Profile', label: 'Users', route: '/users' },
        { key: 'Securities', label: 'Securities', route: '/securities' }
    ];

    // Redux state
    const customers = useAppSelector(selectCustomers);
    const isLoadingCustomers = useAppSelector(selectCustomersLoading);
    const customersError = useAppSelector(selectCustomersError);
    const downloadError = useAppSelector(selectDownloadError);
    const createError = useAppSelector(selectCreateError);
    const userRole = useSelector((state: RootState) => state.auth.userRole);
    // Local state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState<{ key: SortableKeys; direction: 'asc' | 'desc' } | null>({
        key: 'customer',
        direction: 'asc',
    });
    const [selectedCustomerFilter, setSelectedCustomerFilter] = useState<string>('All');
    const [availableCustomers, setAvailableCustomers] = useState<string[]>(['All']);
    const [isCreateCustomerModalOpen, setIsCreateCustomerModalOpen] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);
    // Sales district mapping state
    const [salesDistricts, setSalesDistricts] = useState<SalesDistrictItem[]>([]);
    const [loadingSalesDistricts, setLoadingSalesDistricts] = useState(true);
    // Search functionality
    const { searchText, filteredData: searchedCustomers, handleSearchChange } = useSearch(customers);

    // Function to map district ID to district name
    const getDistrictNameById = (districtId: string): string => {
        if (!districtId || districtId === 'N/A' || !salesDistricts.length) return districtId;
        
        // Try to find by ID first
        const district = salesDistricts.find(d => d.id.toString() === districtId.toString());
        if (district) {
            return district.name;
        }
        
        // If not found by ID, try by name (in case it's already a name)
        const districtByName = salesDistricts.find(d => d.name === districtId);
        if (districtByName) {
            return districtByName.name;
        }
        
        // If still not found, return original value
        console.warn('District not found for ID:', districtId);
        return districtId;
    };

    // Effects
    useEffect(() => {
        const fetchSalesDistricts = async () => {
            try {
                setLoadingSalesDistricts(true);
                const response = await getSalesDistrict();
                if (response.data?.Success && response.data?.Data) {
                    setSalesDistricts(response.data.Data);
                } else {
                    console.error('Failed to fetch sales districts:', response.data?.Message);
                }
            } catch (error) {
                console.error('Error fetching sales districts:', error);
            } finally {
                setLoadingSalesDistricts(false);
            }
        };

        fetchSalesDistricts();
    }, []);

    useEffect(() => {
        if (location.pathname === '/customers') {
            dispatch(fetchCustomerList());
        } else {
            dispatch(resetCustomers());
            setAvailableCustomers(['All']);
            setSelectedCustomerFilter('All');
            setCurrentPage(1);
            setSortConfig({ key: 'customer', direction: 'asc' });
        }
    }, [location.pathname, dispatch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchedCustomers]);

    // Auto-clear create errors after 5 seconds
    useEffect(() => {
        if (createError) {
            const timer = setTimeout(() => {
                dispatch(clearError());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [createError, dispatch]);

    // Clear errors when component unmounts or tab changes
    useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    // Handlers
    const handleDownload = async () => {
        try {
            const response = await dispatch(downloadCustomers()).unwrap();

            // Handle the CSV download
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'customer_data.csv';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\n\r"']*)['"]?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = decodeURIComponent(filenameMatch[1].replace(/\+/g, '%20'));
                }
            }

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch (error) {
            console.error('Error downloading customer CSV:', error);
        }
    };

    const handleCustomerFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedCustomerFilter(event.target.value);
    };

    const requestSort = (key: SortableKeys) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig?.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Use your existing SVG icons for sorting
    const SortIcon = ({ columnKey, className = "" }: { columnKey: SortableKeys; className?: string }) => {
        const isActive = sortConfig && sortConfig.key === columnKey;
        const direction = isActive ? sortConfig.direction : null;
        
        return (
            <div 
                className={`table-sort-icon ${direction ? `sort-${direction}` : ''} ${className}`}
                tabIndex={0}
                role="button"
                aria-label={`Sort by ${columnKey}`}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        requestSort(columnKey);
                    }
                }}
            >
                {!isActive ? (
                    <img src={ArrowDefault} alt="Sort" className="sort-icon-img" />
                ) : direction === 'asc' ? (
                    <img src={ArrowUp} alt="Sort ascending" className="sort-icon-img-up" />
                ) : (
                    <img src={ArrowDown} alt="Sort descending" className="sort-icon-img-down" />
                )}
            </div>
        );
    };

    const getSortArrow = (columnKey: SortableKeys) => {
        return <SortIcon columnKey={columnKey} />;
    };

    const sortedCustomers = useMemo(() => {
        const sortableItems = [...searchedCustomers];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue === undefined || aValue === null) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                if (bValue === undefined || bValue === null) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }

                const valA = String(aValue).toLowerCase();
                const valB = String(bValue).toLowerCase();

                if (valA < valB) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [searchedCustomers, sortConfig]);

    const totalItems = searchedCustomers.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const paginatedCustomers = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedCustomers.slice(startIndex, endIndex);
    }, [sortedCustomers, currentPage, itemsPerPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    const handleOpenCreateCustomerModal = () => {
        dispatch(clearError()); // Clear any existing error state
        setIsCreateCustomerModalOpen(true);
    };

    const handleCloseCreateCustomerModal = () => {
        dispatch(clearError()); // Clear any error state
        setIsCreateCustomerModalOpen(false);
        dispatch(fetchCustomerList()); // Refresh the list when modal closes
    };

    const handleCustomerSaveSuccess = () => {
        dispatch(fetchCustomerList()); // Refresh the list when a customer is saved
        setIsCreateCustomerModalOpen(false);
    };

    // Update error rendering to include create errors
    const renderErrorMessage = () => {
        if (customersError) {
            return (
                <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
                    Error: {customersError}
                </div>
            );
        }
        if (downloadError) {
            return (
                <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
                    Download Error: {downloadError}
                </div>
            );
        }
        // Only show create error if modal is not open (to avoid duplication with toast)
        if (createError && !isCreateCustomerModalOpen) {
            return (
                <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
                    Create Error: {createError}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="onboarded-customer-list-container">
            <TabNavigation tabs={tabConfig} className="tabs-customer" />

            <div className="table-header-customer">
                <div className="header-actions">
                    <div className="search-container-customer">
                        <img className='search-icon-customer-lg' src={SearchIcon} alt='Search' />
                        <input
                            type="text"
                            placeholder="Search"
                            className="search-input-customer"
                            value={searchText}
                            onChange={handleSearchChange}
                            disabled={isLoadingCustomers}
                        />
                    </div>
                </div>

                <button className="download-button-customer" onClick={handleDownload}>
                    <img className="download-icon-lg" src={DownloadIcon} alt="Download" />
                    Download
                </button>
                <button className="create-Customer-button" onClick={handleOpenCreateCustomerModal} disabled={btnDisabled}>
                    Create Customer
                </button>

            </div>

            {renderErrorMessage()}

            <div className="table-wrapper">
                <table className="global-table customer-table">
                    <thead>
                        <tr>
                            <th className="col-customer" onClick={() => requestSort('customer')}>
                                <div className="sort-header">
                                    <span>Customer</span>
                                    {getSortArrow('customer')}
                                </div>
                            </th>
                            <th className="col-sold-to" onClick={() => requestSort('soldTo')}>
                                <div className="sort-header">
                                    <span>Sold to</span>
                                    {getSortArrow('soldTo')}
                                </div>
                            </th>
                            <th className="col-country" onClick={() => requestSort('country')}>
                                <div className="sort-header">
                                    <span>Country</span>
                                    {getSortArrow('country')}
                                </div>
                            </th>
                            <th className="col-sales-district" onClick={() => requestSort('salesDistrict')}>
                                <div className="sort-header">
                                    <span>Sales District</span>
                                    {getSortArrow('salesDistrict')}
                                </div>
                            </th>
                            <th className="col-region" onClick={() => requestSort('salesRegion')}>
                                <div className="sort-header">
                                    <span>Sales Region</span>
                                    {getSortArrow('salesRegion')}
                                </div>
                            </th>
                            <th className="col-state" onClick={() => requestSort('state')}>
                                <div className="sort-header">
                                    <span>State</span>
                                    {getSortArrow('state')}
                                </div>
                            </th>
                            <th className="col-city" onClick={() => requestSort('city')}>
                                <div className="sort-header">
                                    <span>City</span>
                                    {getSortArrow('city')}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoadingCustomers ? (
                            <tr>
                                <td colSpan={7} className="table-loading">
                                    <Loader />
                                </td>
                            </tr>
                        ) : paginatedCustomers.length > 0 ? (
                            paginatedCustomers.map((customer, index) => (
                                <tr key={index}>
                                    <td className="text-wrap">{customer.customer}</td>
                                    <td className="text-wrap">{customer.soldTo}</td>
                                    <td className="text-wrap">{customer.country}</td>
                                    <td className="text-wrap">{getDistrictNameById(customer.salesDistrict)}</td>
                                    <td className="text-wrap">{customer.salesRegion}</td>
                                    <td className="text-wrap">{customer.state}</td>
                                    <td className="text-wrap">{customer.city}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={7} className="table-empty">No Data found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="pagination-fixed-container">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onPreviousPage={handlePreviousPage}
                        onNextPage={handleNextPage}
                    />
                </div>
            </div>
            {isCreateCustomerModalOpen && (
                <CreateAccountModal
                    onClose={handleCloseCreateCustomerModal}
                    onSaveSuccess={handleCustomerSaveSuccess}
                />
            )}
        </div>
    );
};

export default OnboardedCustomerListTable;